import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import Icon from './Icon';
import { Btn } from './ui';
import 'leaflet/dist/leaflet.css';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const RecenterMap = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    map.setView([center.lat, center.lng]);
  }, [center.lat, center.lng, map]);
  return null;
};

// Nominatim Reverse-Geocoding — formatiert eine Adresse aus dem Response
const formatNominatim = (data) => {
  if (!data || !data.address) return data?.display_name || '';
  const a = data.address;
  const street = [a.road, a.house_number].filter(Boolean).join(' ');
  const city = a.city || a.town || a.village || a.municipality || a.suburb || '';
  const postcode = a.postcode || '';
  const cityLine = [postcode, city].filter(Boolean).join(' ');
  return [street, cityLine].filter(Boolean).join(', ') || data.display_name || '';
};

const reverseGeocode = async (lat, lng, signal) => {
  const url = `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&accept-language=de&zoom=18&addressdetails=1`;
  const r = await fetch(url, { signal });
  if (!r.ok) throw new Error('Geocoding fehlgeschlagen');
  return r.json();
};

const LocationPicker = ({ isVisible, onLocationSelect, onClose, initialLocation }) => {
  const fallback = { lat: 50.9967, lng: 6.5711 };
  const [selected, setSelected] = useState(initialLocation || fallback);
  const [zoom, setZoom] = useState(initialLocation?.zoom || 13);
  const [adresse, setAdresse] = useState('');
  const [loadingAdresse, setLoadingAdresse] = useState(false);
  const abortRef = useRef(null);

  useEffect(() => {
    if (isVisible && initialLocation) {
      setSelected({ lat: initialLocation.lat, lng: initialLocation.lng });
      if (initialLocation.zoom) setZoom(initialLocation.zoom);
      setAdresse('');
    }
  }, [initialLocation, isVisible]);

  // Reverse-Geocode bei jeder Auswahl
  useEffect(() => {
    if (!isVisible) return undefined;
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoadingAdresse(true);
    setAdresse('');
    const timer = setTimeout(async () => {
      try {
        const data = await reverseGeocode(selected.lat, selected.lng, ctrl.signal);
        setAdresse(formatNominatim(data));
      } catch (e) {
        if (e.name !== 'AbortError') console.error('Nominatim:', e);
      } finally {
        setLoadingAdresse(false);
      }
    }, 350); // kleines Debounce, um Klick-Spam zu dämpfen
    return () => {
      clearTimeout(timer);
      ctrl.abort();
    };
  }, [selected.lat, selected.lng, isVisible]);

  const Click = () => {
    useMapEvents({
      click: (e) => setSelected({ lat: e.latlng.lat, lng: e.latlng.lng }),
    });
    return <Marker position={[selected.lat, selected.lng]} />;
  };

  if (!isVisible) return null;

  return (
    <div className="lp-modal">
      <div className="lp-content">
        <div className="lp-header">
          <h3>
            <Icon name="location_on" size={20} color="var(--amber)" /> Ort auf Karte auswählen
          </h3>
          <button type="button" onClick={onClose} className="lp-close" aria-label="Schließen">
            <Icon name="close" size={20} />
          </button>
        </div>

        <div className="lp-map">
          <MapContainer
            center={[selected.lat, selected.lng]}
            zoom={zoom}
            style={{ height: '420px', width: '100%' }}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; OpenStreetMap'
            />
            <Click />
            <RecenterMap center={selected} />
          </MapContainer>
        </div>

        <div className="lp-info">
          <div style={{ minWidth: 0 }}>
            <div className="lp-info-label">Adresse (automatisch)</div>
            <div className="lp-info-address">
              {loadingAdresse
                ? 'Adresse wird ermittelt …'
                : adresse || 'Keine Adresse gefunden'}
            </div>
            <div className="lp-info-coords">
              {selected.lat.toFixed(6)}, {selected.lng.toFixed(6)}
            </div>
          </div>
          <div className="lp-info-zoom">
            <div className="lp-info-label">Zoom (1–18)</div>
            <input
              type="number"
              min="1"
              max="18"
              value={zoom}
              onChange={(e) => setZoom(parseInt(e.target.value, 10) || 13)}
              className="lp-zoom-input"
            />
          </div>
        </div>

        <div className="lp-actions">
          <Btn variant="ghost" onClick={onClose}>Abbrechen</Btn>
          <Btn
            variant="primary"
            icon="check"
            onClick={() => {
              onLocationSelect({ ...selected, zoom, adresse });
              onClose();
            }}
          >
            Bestätigen
          </Btn>
        </div>
      </div>
    </div>
  );
};

export default LocationPicker;
